import type {CmLogger} from "@cm/log"
import type {RawPolyMeshData} from "#template/geometry-processing/raw-poly-mesh-data"
import {Matrix4, Vector3} from "@cm/math"
import * as FBXParser from "fbx-parser"

export type FbxMeshData = RawPolyMeshData & {
    name: string
}

type FbxNode = FBXParser.FBXNode

type LayerElementData = {
    mappingType: string
    referenceType: string
    directArray: number[] | null
    indexArray: number[] | null
    componentCount: number
}

export function extractMeshData(fbxNodes: FBXParser.FBXData, logger: CmLogger): FbxMeshData[] {
    const connectionsNode = fbxNodes.find((n) => n.name === "Connections")
    if (!connectionsNode) {
        throw new Error("No 'Connections' node found in FBX data.")
    }

    const globalSettings = fbxNodes.find((n) => n.name === "GlobalSettings")
    const properties = globalSettings?.nodes.find((n) => n.name === "Properties70")

    const unitScale = (properties?.nodes.find((n) => n.name === "P" && n.props[0] === "UnitScale")?.props[4] as number | undefined) ?? 1
    const frontAxis = (properties?.nodes.find((n) => n.name === "P" && n.props[0] === "FrontAxis")?.props[4] as number | undefined) ?? 1
    const frontAxisSign = (properties?.nodes.find((n) => n.name === "P" && n.props[0] === "FrontAxisSign")?.props[4] as number | undefined) ?? 1
    const upAxis = (properties?.nodes.find((n) => n.name === "P" && n.props[0] === "UpAxis")?.props[4] as number | undefined) ?? 2
    const upAxisSign = (properties?.nodes.find((n) => n.name === "P" && n.props[0] === "UpAxisSign")?.props[4] as number | undefined) ?? 1
    const coordAxis = (properties?.nodes.find((n) => n.name === "P" && n.props[0] === "CoordAxis")?.props[4] as number | undefined) ?? 0
    const coordAxisSign = (properties?.nodes.find((n) => n.name === "P" && n.props[0] === "CoordAxisSign")?.props[4] as number | undefined) ?? 1

    //TODO: Add support for other axis systems / non-unit scaling
    if (frontAxis !== 2 || frontAxisSign !== 1 || upAxis !== 1 || upAxisSign !== 1 || coordAxis !== 0 || coordAxisSign !== 1) {
        throw new Error("Non-standard axis system is not yet supported")
    }

    const objectsNode = fbxNodes.find((n) => n.name === "Objects")
    if (!objectsNode) {
        throw new Error("No 'Objects' node found in FBX data.")
    }

    const connectionNodes = findNodesByName(connectionsNode.nodes, "C")

    const processNode = (nodeId: number, parentMatrix: Matrix4, parentName: string | undefined) => {
        const objectNode = objectsNode.nodes.find((n) => n.props[0] === nodeId)
        const properties = objectNode?.nodes.find((n) => n.name === "Properties70")

        const geometricTranslation = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "GeometricTranslation")
        const geometricRotation = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "GeometricRotation")
        const geometricScaling = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "GeometricScaling")

        const rotationActive = (properties?.nodes.find((n) => n.name === "P" && n.props[0] === "RotationActive")?.props[4] as boolean | undefined) ?? false
        const rotationOffset = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "RotationOffset")
        const rotationPivot = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "RotationPivot")
        const preRotation = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "PreRotation")
        const rotationOrder = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "RotationOrder")
        const rotation = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "Lcl Rotation")
        const postRotation = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "PostRotation")

        const translation = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "Lcl Translation")

        const scalingOffset = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "ScalingOffset")
        const scalingPivot = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "ScalingPivot")
        const scale = properties?.nodes.find((n) => n.name === "P" && n.props[0] === "Lcl Scaling")

        //FBX transform: https://help.autodesk.com/view/FBX/2017/ENU/?guid=__files_GUID_10CDD63C_79C1_4F2D_BB28_AD2BE65A02ED_htm

        if (rotationOffset || rotationPivot) throw new Error("Rotation offset and pivot not yet supported")
        if (scalingOffset || scalingPivot) throw new Error("Scaling offset and pivot not yet supported")

        let transformMatrix = parentMatrix.copy()
        if (translation)
            transformMatrix = transformMatrix.multiply(
                Matrix4.translation(translation.props[4] as number, translation.props[5] as number, translation.props[6] as number),
            )
        if (rotationActive && preRotation) {
            const rx = preRotation.props[4] as number
            const ry = preRotation.props[5] as number
            const rz = preRotation.props[6] as number
            transformMatrix = transformMatrix.multiply(Matrix4.rotationZ(rz).multiply(Matrix4.rotationY(ry)).multiply(Matrix4.rotationX(rx)))
        }
        if (rotation) {
            if (rotationOrder && rotationOrder.props[4] !== 0) throw new Error("Rotation order not supported")
            const rx = rotation.props[4] as number
            const ry = rotation.props[5] as number
            const rz = rotation.props[6] as number
            transformMatrix = transformMatrix.multiply(Matrix4.rotationZ(rz).multiply(Matrix4.rotationY(ry)).multiply(Matrix4.rotationX(rx)))
        }
        if (rotationActive && postRotation) {
            const rx = postRotation.props[4] as number
            const ry = postRotation.props[5] as number
            const rz = postRotation.props[6] as number
            transformMatrix = transformMatrix.multiply(Matrix4.rotationZ(rz).multiply(Matrix4.rotationY(ry)).multiply(Matrix4.rotationX(rx)).inverse())
        }
        if (scale) transformMatrix = transformMatrix.multiply(Matrix4.scaling(scale.props[4] as number, scale.props[5] as number, scale.props[6] as number))

        //Geometric transformations are not inherited: ParentWorldTransform does not contain the OT, OR, and OS of WorldTransform’s parent node.
        let geometryTransformMatrix = transformMatrix.copy()

        if (geometricTranslation)
            geometryTransformMatrix = geometryTransformMatrix.multiply(
                Matrix4.translation(geometricTranslation.props[4] as number, geometricTranslation.props[5] as number, geometricTranslation.props[6] as number),
            )
        if (geometricRotation) {
            const rx = geometricRotation.props[4] as number
            const ry = geometricRotation.props[5] as number
            const rz = geometricRotation.props[6] as number
            geometryTransformMatrix = geometryTransformMatrix.multiply(Matrix4.rotationZ(rz).multiply(Matrix4.rotationY(ry)).multiply(Matrix4.rotationX(rx)))
        }
        if (geometricScaling)
            geometryTransformMatrix = geometryTransformMatrix.multiply(
                Matrix4.scaling(geometricScaling.props[4] as number, geometricScaling.props[5] as number, geometricScaling.props[6] as number),
            )

        let newParentName = parentName
        if (objectNode) {
            switch (objectNode.name) {
                case "Geometry": {
                    processGeometry(objectNode, geometryTransformMatrix, parentName)
                    break
                }
                case "Model": {
                    const [_objectID, modelName, _modelType] = objectNode.props
                    newParentName = (modelName as string).replace(/^Model::/, "")
                    break
                }
            }
        }

        for (const connection of connectionNodes) {
            const [connectionType, sourceId, destinationId] = connection.props
            if (connectionType !== "OO") continue

            if (destinationId === nodeId) {
                processNode(sourceId as number, transformMatrix, newParentName)
            }
        }
    }

    const results: FbxMeshData[] = []

    const processGeometry = (geometryNode: FBXParser.FBXNode, transformation: Matrix4, modelName: string | undefined) => {
        const [objectID, geomName, geomType] = geometryNode.props
        if (geomType !== "Mesh") return
        const name = (() => {
            const tmp = (geomName as string).replace(/^Geometry::/, "")
            if (modelName && modelName !== "") {
                if (tmp && tmp !== "") return `${modelName}_${tmp}`
                else return modelName
            }
            return tmp || ""
        })()

        const validLayerElementData = (data: LayerElementData): data is LayerElementData & {directArray: number[]} => {
            return data.directArray !== null && (data.referenceType === "Direct" || (data.referenceType === "IndexToDirect" && data.indexArray !== null))
        }

        try {
            const vertexData = getVertexLayer(geometryNode)
            if (!validLayerElementData(vertexData)) {
                throw new Error("Invalid vertex layer data in Geometry.")
            }

            const polygons = getPolygonIndices(geometryNode)
            const numPolygons = polygons.length
            const polygonVertexCount = polygons.reduce((sum, poly) => sum + poly.length, 0)

            // Layer elements
            const materialData = findMaterialLayer(geometryNode) ?? {
                mappingType: "AllSame",
                referenceType: "Direct",
                directArray: [0],
                indexArray: null,
                componentCount: 1,
            }
            const normalData = findNormalLayer(geometryNode)
            const uvData = findUVLayers(geometryNode).filter(validLayerElementData)

            if (!validLayerElementData(materialData)) {
                throw new Error(`No valid material layer found.`)
            }

            if (!normalData || !validLayerElementData(normalData)) {
                throw new Error(`No valid normal layer found.`)
            }

            // Prepare final arrays
            const verticesPerFace = new Uint32Array(numPolygons)
            const positions = new Float32Array(vertexData.directArray)
            const positionIndices = new Uint32Array(polygonVertexCount)
            const normals = new Float32Array(normalData.directArray)
            const normalIndices = new Uint32Array(polygonVertexCount)
            const uvs = uvData.map((uvData) => new Float32Array(uvData.directArray))
            const uvIndices = uvs.map(() => new Uint32Array(polygonVertexCount))
            const materialIndices = new Uint32Array(polygonVertexCount)

            // Fill data
            let pvCounter = 0
            for (let p = 0; p < numPolygons; p++) {
                const poly = polygons[p]
                verticesPerFace[p] = poly.length

                for (let v = 0; v < poly.length; v++) {
                    const vertexIndex = poly[v]

                    positionIndices[pvCounter] = getElementIndex(
                        vertexData.mappingType,
                        vertexData.referenceType,
                        vertexData.indexArray,
                        vertexIndex,
                        p,
                        pvCounter,
                    )

                    // Material

                    materialIndices[pvCounter] =
                        materialData.directArray[
                            getElementIndex(materialData.mappingType, materialData.referenceType, materialData.indexArray, vertexIndex, p, pvCounter)
                        ]

                    // Normals
                    normalIndices[pvCounter] = getElementIndex(
                        normalData.mappingType,
                        normalData.referenceType,
                        normalData.indexArray,
                        vertexIndex,
                        p,
                        pvCounter,
                    )

                    // UVs
                    for (let i = 0; i < uvData.length; i++) {
                        const currentUvData = uvData[i]
                        const currentUvDataIndex = uvIndices[i]
                        currentUvDataIndex[pvCounter] = getElementIndex(
                            currentUvData.mappingType,
                            currentUvData.referenceType,
                            currentUvData.indexArray,
                            vertexIndex,
                            p,
                            pvCounter,
                        )
                    }

                    pvCounter++
                }
            }

            for (let i = 0; i < positions.length; i += 3) {
                const x = positions[i]
                const y = positions[i + 1]
                const z = positions[i + 2]
                const transformed = transformation.multiplyVectorXYZW(x, y, z, 1.0)
                positions[i] = transformed[0]
                positions[i + 1] = transformed[1]
                positions[i + 2] = transformed[2]
            }

            const normalMatrix = transformation.inverse().transpose()
            for (let i = 0; i < normals.length; i += 3) {
                const x = normals[i]
                const y = normals[i + 1]
                const z = normals[i + 2]
                const transformed = normalMatrix.multiplyVectorXYZW(x, y, z, 0.0)
                const normal = new Vector3(transformed[0], transformed[1], transformed[2]).normalized()
                normals[i] = normal.x
                normals[i + 1] = normal.y
                normals[i + 2] = normal.z
            }

            let materialIds: Uint32Array | null = null
            {
                const materialIdToIdxMap = new Map<number, number>()
                for (let i = 0; i < materialIndices.length; i++) {
                    const index = materialIndices[i]
                    const mappedIndex = materialIdToIdxMap.get(index)
                    if (mappedIndex === undefined) {
                        materialIdToIdxMap.set(index, materialIdToIdxMap.size)
                        materialIndices[i] = materialIdToIdxMap.get(index)!
                    } else materialIndices[i] = mappedIndex
                }

                materialIds = new Uint32Array(materialIdToIdxMap.size)
                for (let i = 0; i < materialIds.length; i++) {
                    materialIds[i] = i
                }
            }

            const result: FbxMeshData = {
                name: name === "" ? `Mesh_${objectID}` : name,
                polyVertexCount: verticesPerFace,
                position: {
                    elementType: "float32",
                    elementCount: 3,
                    values: positions,
                    indices: positionIndices,
                    consolidate: false,
                },
                materialID: {
                    elementType: "uint32",
                    elementCount: 1,
                    values: materialIds,
                    indices: materialIndices,
                    consolidate: materialData.indexArray === null,
                },
                normal: {
                    elementType: "float32",
                    elementCount: 3,
                    values: normals,
                    indices: normalIndices,
                    consolidate: normalData.indexArray === null,
                },
                uvs: uvs.map((uvsArray, i) => ({
                    elementType: "float32",
                    elementCount: 2,
                    values: uvsArray,
                    indices: uvIndices[i],
                    consolidate: uvData[i].indexArray === null,
                })),
            }
            results.push(result)
        } catch (e) {
            if (e instanceof Error) logger.error(`Failed to process geometry ${name}, skipping due to: ${e.message}`)
            else {
                throw e
            }
        }
    }

    processNode(0, Matrix4.scaling(unitScale, unitScale, unitScale), undefined) // Process root node with id 0

    return results
}

function getVertexLayer(geomNode: FbxNode): LayerElementData {
    const verticesNode = findNodeByName(geomNode.nodes, "Vertices")
    if (!verticesNode) throw new Error("No Vertices node found in Geometry.")
    const vertexArray = verticesNode.props[0] as number[]

    return {
        mappingType: "ByControlPoint",
        referenceType: "Direct",
        directArray: vertexArray,
        indexArray: null,
        componentCount: 3,
    }
}

function getPolygonIndices(geomNode: FbxNode): number[][] {
    const polygonIndexNode = findNodeByName(geomNode.nodes, "PolygonVertexIndex")
    if (!polygonIndexNode) throw new Error("No PolygonVertexIndex node found in Geometry.")
    const polygonIndices = polygonIndexNode.props[0] as number[]

    const polygons: number[][] = []
    let currentPoly: number[] = []
    for (let i = 0; i < polygonIndices.length; i++) {
        const idx = polygonIndices[i]
        if (idx < 0) {
            const v = -idx - 1
            currentPoly.push(v)
            polygons.push(currentPoly)
            currentPoly = []
        } else {
            currentPoly.push(idx)
        }
    }
    return polygons
}

function findMaterialLayer(geomNode: FbxNode): LayerElementData | null {
    const layerNode = findNodeByName(geomNode.nodes, "LayerElementMaterial")
    if (!layerNode) return null
    const layer = getLayerElementData(layerNode, "Materials", "MaterialIndex", 1)
    // Usually referenceType is "IndexToDirect" for materials, meaning index is direct material ID.
    return {
        ...layer,
        referenceType: layer.referenceType === "IndexToDirect" && layer.indexArray === null ? "Direct" : layer.referenceType,
    }
}

function findNormalLayer(geomNode: FbxNode): LayerElementData | null {
    const layerNode = findNodeByName(geomNode.nodes, "LayerElementNormal")
    if (!layerNode) return null
    return getLayerElementData(layerNode, "Normals", "NormalsIndex", 3)
}

function findUVLayers(geomNode: FbxNode): LayerElementData[] {
    const layerNodes = findNodesByName(geomNode.nodes, "LayerElementUV")
    return layerNodes.map((layerNode) => getLayerElementData(layerNode, "UV", "UVIndex", 2))
}

function getLayerElementData(layerNode: FbxNode, directNodeName: string, indexNodeName: string, componentCount: number): LayerElementData {
    const mappingType = (findNodeByName(layerNode.nodes, "MappingInformationType")?.props[0] as string | undefined) || "ByPolygonVertex"
    const referenceType = (findNodeByName(layerNode.nodes, "ReferenceInformationType")?.props[0] as string | undefined) || "Direct"

    const directNode = findNodeByName(layerNode.nodes, directNodeName)
    const indexNode = findNodeByName(layerNode.nodes, indexNodeName)

    const directArray = directNode?.props[0] as number[] | undefined
    const indexArray = indexNode ? (indexNode.props[0] as number[]) : null

    return {
        mappingType,
        referenceType,
        directArray: directArray ?? null,
        indexArray,
        componentCount,
    }
}

function getElementIndex(
    mappingType: string,
    referenceType: string,
    indexArray: number[] | null,
    controlPointIndex: number,
    polygonIndex: number,
    polygonVertexIndex: number,
): number {
    let finalIndex: number

    switch (mappingType) {
        case "ByControlPoint":
        case "ByVertex":
        case "ByVertice":
            finalIndex = controlPointIndex
            break
        case "ByPolygonVertex":
            finalIndex = polygonVertexIndex
            break
        case "ByPolygon":
            finalIndex = polygonIndex
            break
        case "AllSame":
            finalIndex = 0
            break
        case "ByEdge":
            throw new Error("MappingInformationType 'ByEdge' not supported.")
        default:
            throw new Error(`Unsupported MappingInformationType: ${mappingType}`)
    }

    if (referenceType === "IndexToDirect") {
        if (!indexArray) throw new Error("Index array missing for IndexToDirect reference")
        finalIndex = indexArray[finalIndex]
    } else if (referenceType !== "Direct") {
        throw new Error(`Unsupported ReferenceInformationType: ${referenceType}`)
    }

    return finalIndex
}

function findNodeByName(nodes: FbxNode[], name: string): FbxNode | undefined {
    return nodes.find((n) => n.name === name)
}

function findNodesByName(nodes: FbxNode[], name: string): FbxNode[] {
    return nodes.filter((n) => n.name === name)
}

export function importMeshesFromFBXFile(buffer: ArrayBufferView, logger: CmLogger) {
    let fbx: FBXParser.FBXData
    try {
        fbx = FBXParser.parseBinary(new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength))
    } catch (e) {
        logger.warn("Failed to parse FBX file as binary, trying text parser")
        fbx = FBXParser.parseText(new TextDecoder().decode(buffer))
    }
    return extractMeshData(fbx, logger)
}
